import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Paper, AppBar, Toolbar, Chip, Button, IconButton, Avatar, Snackbar, Alert, InputBase, Collapse, AvatarGroup, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { motion } from 'framer-motion';
import { CATALOG, TRACKS } from './data/catalog';
import { useFlowstateAuth, getInitials } from './context/AuthContext';
import { useThemeContext } from './context/ThemeContext';
import { SHOW_PAYMENT_FLOW, usePricing } from './data/pricing';
import { BrandLogo } from './components/BrandLogo';
import { LogoutConfirmDialog } from './components/LogoutConfirmDialog';
import { FoundationRoadmap } from './components/Roadmap/FoundationRoadmap';

// Badge colour map
const BADGE_COLORS = {
  'Array/String': { bg: '#1e3a8a18', color: '#60a5fa', border: '#3b82f630' },
  'Heap': { bg: '#7c3aed18', color: '#a78bfa', border: '#7c3aed30' },
  'Tree/Graph': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Tree': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Graph': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Graph/Matrix': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Search': { bg: '#92400e18', color: '#fbbf24', border: '#f59e0b30' },
  'Stack': { bg: '#9f123318', color: '#fb7185', border: '#f4365530' },
  'DP': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
  'Math/Array': { bg: '#1e3a8a18', color: '#60a5fa', border: '#3b82f630' },
  'Greedy': { bg: '#92400e18', color: '#fbbf24', border: '#f59e0b30' },
  'Backtracking': { bg: '#9f123318', color: '#fb7185', border: '#f4365530' },
  'Fundamentals': { bg: '#1e3a8a18', color: '#60a5fa', border: '#3b82f630' },
  'Data Layer': { bg: '#7c3aed18', color: '#a78bfa', border: '#7c3aed30' },
  'Advanced': { bg: '#92400e18', color: '#fbbf24', border: '#f59e0b30' },
  'Distributed': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Security': { bg: '#9f123318', color: '#fb7185', border: '#f4365530' },
  'Patterns': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
  'Ops': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  'Core': { bg: '#1e3a8a18', color: '#60a5fa', border: '#3b82f630' },
  'Architect': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
  'Expert': { bg: '#92400e18', color: '#fbbf24', border: '#f59e0b30' },
  'Q&A': { bg: '#065f4618', color: '#34d399', border: '#10b98130' },
  // Newly Added Frontend Prep topics
  'Async': { bg: '#f59e0b18', color: '#fbbf24', border: '#f59e0b30' },
  'Browser': { bg: '#0ea5e918', color: '#38bdf8', border: '#0ea5e930' },
  'Ecosystem': { bg: '#10b98118', color: '#34d399', border: '#10b98130' },
  'React': { bg: '#0ea5e918', color: '#38bdf8', border: '#0ea5e930' },
  'Hooks': { bg: '#f43f5e18', color: '#fb7185', border: '#f43f5e30' },
  'Architecture': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
  'Performance': { bg: '#f43f5e18', color: '#fb7185', border: '#f43f5e30' },
  'CSS': { bg: '#ec489918', color: '#f472b6', border: '#ec489930' },
  'TS': { bg: '#3b82f618', color: '#60a5fa', border: '#3b82f630' },
  'QA': { bg: '#10b98118', color: '#34d399', border: '#10b98130' },
  'a11y': { bg: '#ec489918', color: '#f472b6', border: '#ec489930' },
  'Next.js': { bg: '#71717a18', color: '#a1a1aa', border: '#71717a30' },
  'Basics': { bg: '#3b82f618', color: '#60a5fa', border: '#3b82f630' },
  'Next-Gen': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
  'Network': { bg: '#0ea5e918', color: '#38bdf8', border: '#0ea5e930' },
  'Caching': { bg: '#f59e0b18', color: '#fbbf24', border: '#f59e0b30' },
  'Forms': { bg: '#10b98118', color: '#34d399', border: '#10b98130' },
  'Senior': { bg: '#4c1d9518', color: '#c084fc', border: '#a855f730' },
};

const getBadgeStyle = (badge, isLight) =>
  BADGE_COLORS[badge] ?? (isLight ? { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' } : { bg: '#18181b', color: '#a1a1aa', border: '#27272a' });

// Static — defined outside component so it is never recreated on re-renders
const TRACK_BADGES = {
  javascript: { label: '★ MOST POPULAR', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: 'rgba(245,158,11,0.4)' },
  react: { label: '★ BESTSELLER', gradient: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', shadow: 'rgba(14,165,233,0.4)' },
  angular: { label: '★ ENTERPRISE', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', shadow: 'rgba(99,102,241,0.4)' },
  frontendSystemDesign: { label: '★ RECOMMENDED', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: 'rgba(16,185,129,0.4)' },
  interviewPrep: { label: '★ BESTSELLER', gradient: 'linear-gradient(135deg, #db2777 0%, #ec4899 100%)', shadow: 'rgba(219,39,119,0.4)' },
};

export const TopicSelection = ({ injectedCategoryId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const categoryId = injectedCategoryId || params.categoryId;
  const { user, logout, createOrder, verifyPayment } = useFlowstateAuth();
  const { mode, toggleTheme } = useThemeContext();
  const { getPrice } = usePricing();
  const isLight = mode === 'light';
  const categoryData = categoryId ? CATALOG[categoryId] : null;

  // Memoized: only recomputes when the catalog data for this category changes
  const previewIds = useMemo(() => {
    const ids = [];
    if (categoryData && categoryData.topics) {
      categoryData.topics.forEach(topic => {
        ids.push(topic.id);
        if (topic.problems && topic.problems.length > 0) {
          topic.problems.forEach(p => ids.push(p.id));
        }
      });
    }
    const limit = categoryId === 'interviewPrep' ? 6 : (categoryId === 'dsa' ? 5 : 4);
    return ids.slice(0, limit);
  }, [categoryData, categoryId]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [expandedMods, setExpandedMods] = useState({});

  const trackId = Object.keys(TRACKS).find(tid => TRACKS[tid].categories.includes(categoryId)) || 'frontend';
  const basePath = location.pathname.replace(/\/$/, "");

  const isFree = !SHOW_PAYMENT_FLOW;
  const isPurchased = useMemo(() => {
    const purchased = user?.purchasedModules || [];
    if (purchased.includes(categoryId || '')) return true;

    // Implicit unlock: Check if the user owns any TRACK that contains this category
    for (const trackKey of Object.keys(TRACKS)) {
      if (purchased.includes(trackKey)) {
        if (TRACKS[trackKey].categories?.includes(categoryId)) {
          return true;
        }
      }
    }
    return false;
  }, [user, categoryId]);

  const isBundleAdvantageous = useMemo(() => {
    if (!categoryData?.isTrackLink) return true;

    const childCats = TRACKS[categoryId]?.categories || [];
    let remainingCost = 0;

    childCats.forEach(modKey => {
      const purchased = user?.purchasedModules || [];
      const isOwned = (isFree && !!user) || purchased.includes(modKey) || isPurchased;
      if (!isOwned) {
        remainingCost += getPrice(modKey).price || 0;
      }
    });

    const bundlePrice = getPrice(categoryId).price || 0;

    // Hide bundle if the remaining individual modules are cheaper or equal to the master bundle price
    return remainingCost === 0 ? false : remainingCost >= bundlePrice;
  }, [categoryData, categoryId, user, isPurchased, getPrice, isFree]);

  // Memoized: derive the ordered list of group names from catalog — avoids Set+forEach on every render
  const orderedGroups = useMemo(() => {
    if (!categoryData || !categoryData.topics) return [];
    const seen = new Set();
    const groups = [];
    categoryData.topics.forEach(t => {
      if (!seen.has(t.group)) {
        seen.add(t.group);
        groups.push(t.group);
      }
    });
    if (!seen.has(undefined)) groups.push(undefined);
    return groups;
  }, [categoryData]);

  const [loadingProductId, setLoadingProductId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Load Razorpay Script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handlePurchase = async () => {
    if (!categoryId || !categoryData) return;
    if (!user) {
      navigate(`/interview-prep/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    try {
      setLoadingProductId(categoryId);
      const order = await createOrder(categoryId);

      if (!window.Razorpay) {
        throw new Error('Payment system is not loaded. Please refresh the page and try again.');
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'FlowState',
        description: `Unlock ${categoryId}`,
        order_id: order.id,
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              moduleId: categoryId
            });
            setAlertMessage(`Successfully unlocked ${categoryId}!`);
            setAlertOpen(true);
          } catch (verifyErr) {
            setAlertMessage(verifyErr.message || 'Verification failed.');
            setAlertOpen(true);
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#2563eb'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setAlertMessage(response.error.description || 'Payment failed.');
        setAlertOpen(true);
      });
      rzp.open();

    } catch (err) {
      console.error('Purchase Error:', err);
      setAlertMessage(err.message || 'Something went wrong with the payment');
      setAlertOpen(true);
    } finally {
      setLoadingProductId(null);
    }
  };

  if (!categoryData) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: isLight
        ? 'radial-gradient(ellipse at 50% 0%, #cbd5e1 0%, #f8fafc 65%)'
        : 'radial-gradient(ellipse at 50% 0%, #1c1c2e 0%, #0a0a0c 65%)',
    }}>

      {/* ── Navbar ── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 3, md: 6 }, minHeight: '64px !important' }}>
          <Box
            onClick={() => navigate('/interview-prep')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <BrandLogo size="medium" isLight={isLight} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              onClick={() => {
                const segs = basePath.split('/').filter(Boolean);
                if (segs.length > 2) {
                  segs.pop();
                  navigate('/' + segs.join('/'));
                } else {
                  navigate('/interview-prep');
                }
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#52525b', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, transition: 'color 0.15s', '&:hover': { color: '#e2e8f0' }, mr: 1 }}
            >
              <ArrowBackIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.4px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Back
              </Typography>
            </Box>

            <Tooltip title={isLight ? "Switch to Dark Mode" : "Switch to Light Mode"} arrow>
              <IconButton onClick={toggleTheme} sx={{ color: isLight ? '#0f172a' : '#f1f5f9' }}>
                {isLight ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            {user ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Tooltip title={`Signed in as ${user.first_name || 'User'}`} arrow>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.85rem', fontWeight: 700 }}>
                    {getInitials(user)}
                  </Avatar>
                </Tooltip>
                <Tooltip title="Logout" arrow>
                  <IconButton onClick={() => setLogoutDialogOpen(true)} size="small" sx={{ color: isLight ? '#64748b' : '#a1a1aa' }}>
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Button
                variant="outlined"
                onClick={() => navigate(`/interview-prep/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  color: isLight ? '#0f172a' : '#f1f5f9',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'transparent' }
                }}
              >
                Log In
              </Button>
            )}
          </Box>
        </Toolbar>
        <LogoutConfirmDialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
          onConfirm={() => {
            setLogoutDialogOpen(false);
            logout();
          }}
        />
      </AppBar>

      {/* ── Main ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', px: { xs: 3, md: 6 }, py: 5, overflowY: 'auto' }}>
        <Box sx={{ maxWidth: 1100, width: '100%', mx: 'auto' }}>

          {/* Hero */}
          <Box sx={{ mb: 5, textAlign: 'center' }}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ '& svg': { fontSize: '28px !important' } }}>{categoryData.icon}</Box>
            </Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: isLight ? '#0f172a' : '#f1f5f9', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.75 }}>
              {categoryData.title}
            </Typography>
            <Typography variant="body2" sx={{ color: isLight ? '#475569' : '#71717a', fontSize: '0.9rem' }}>
              Select a module below to launch the visualization engine.
            </Typography>
          </Box>

          {/* ── Dashboard for AI Interview Prep (Exact 3 Cards Requested) ── */}
          {categoryId === 'aiInterviewPrep' && (
            <Box sx={{ mb: 10 }}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
                gap: 4,
                mb: 6
              }}>
                {['dsa', 'aiFundamentals', 'aiEngineering'].map((modKey, idx) => {
                  const modData = CATALOG[modKey];
                  if (!modData) return null;

                  const modColors = {
                    dsa: '#8b5cf6',
                    aiFundamentals: '#f1c40f',
                    aiEngineering: '#ef4444'
                  };
                  const primaryColor = modColors[modKey];
                  const isComingSoon = modKey !== 'dsa';

                  return (
                    <motion.div
                      key={modKey}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      style={{ display: 'flex' }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          p: 4,
                          borderRadius: '24px',
                          background: isLight ? '#ffffff' : 'rgba(23, 23, 28, 1)',
                          border: isLight ? `2px solid ${primaryColor}20` : `2px solid ${primaryColor}40`,
                          position: 'relative',
                          overflow: 'visible',
                          transition: 'all 0.3s ease',
                          cursor: modKey === 'dsa' ? 'pointer' : 'default',
                          opacity: modKey === 'dsa' ? 1 : 0.75,
                          '&:hover': modKey === 'dsa' ? {
                            transform: 'translateY(-8px)',
                            boxShadow: `0 30px 60px -15px ${primaryColor}40`,
                            borderColor: primaryColor
                          } : {}
                        }}
                        onClick={() => {
                          const segs = location.pathname.split('/').filter(Boolean);
                          segs.pop();
                          segs.push(modKey);
                          navigate('/' + segs.join('/'));
                        }}
                      >
                        {modKey === 'dsa' ? (
                          <Box sx={{
                            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #db2777 0%, #ec4899 100%)',
                            color: '#fff', px: 2, py: 0.75, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 0.75,
                            boxShadow: '0 8px 20px rgba(219, 39, 119, 0.4)', zIndex: 10
                          }}>
                            <StarIcon sx={{ fontSize: 13 }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '1.2px', textTransform: 'uppercase' }}>RECOMMENDED</Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                            background: isLight ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                            backdropFilter: 'blur(10px)',
                            color: isLight ? '#2563eb' : '#60a5fa', px: 2, py: 0.75, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 0.75,
                            border: `1px solid ${primaryColor}40`,
                            zIndex: 10
                          }}>
                            <FlashOnIcon sx={{ fontSize: 13 }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '1.2px', textTransform: 'uppercase' }}>COMING SOON</Typography>
                          </Box>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="overline" sx={{ color: primaryColor, fontWeight: 800, letterSpacing: '1px' }}>
                            {modKey === 'dsa' ? 'ALGORITHM MASTERY' : modKey === 'aiFundamentals' ? 'AI FOUNDATIONS' : 'PRODUCTION AI'}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 900, color: isLight ? '#0f172a' : '#f8fafc', fontSize: '1.45rem', lineHeight: 1.1, mb: 2 }}>
                            {modData.title}
                          </Typography>
                        </Box>

                        <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '0.9rem', mb: 4, flex: 1, fontWeight: 500 }}>
                          {modData.description || modData.desc}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                          {(modData.features?.length ? modData.features : (modKey === 'dsa' ? ['500+ Curated Problems', 'Frame-by-Frame Visualization', 'Optimal Complexity Analysis', 'Blind 75 & Grind 169'] : modKey === 'aiFundamentals' ? ['Neural Network Basics', 'Deep Learning Concepts', 'Math for AI'] : ['LLM Application Patterns', 'RAG Architectures', 'Agentic Workflows'])).slice(0, 4).map((feat, fidx) => (
                            <Box key={fidx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: primaryColor }} />
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#334155' : '#cbd5e1' }}>
                                {feat}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {!((isFree && !!user) || user?.purchasedModules?.includes(modKey)) && (
                          <Box sx={{ borderTop: isLight ? '1px dashed #e2e8f0' : '1px dashed #27272a', py: 2.5, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: isLight ? '#475569' : '#a1a1aa', letterSpacing: '1px' }}>
                                QUICK SYLLABUS PEEK
                              </Typography>
                              <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} />
                            </Box>
                          </Box>
                        )}

                        {!isComingSoon && (
                          <Box sx={{ mt: 'auto', pt: 3, borderTop: isLight ? '1px solid #f1f5f9' : '1px solid #1f1f22', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: '0.6rem', border: '1px solid #1c1c2e' } }}>
                                {[1, 2, 3, 4].map(i => <Avatar key={i} src={`https://i.pravatar.cc/100?u=${i + idx * 10}`} />)}
                              </AvatarGroup>
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: isLight ? '#64748b' : '#71717a' }}>
                                +{700 + ((modKey || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 200)} ENROLLED
                              </Typography>
                            </Box>

                            <Box sx={{ px: 1.2, py: 0.5, borderRadius: '8px', bgcolor: `${primaryColor}15`, border: `1px solid ${primaryColor}30`, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocalFireDepartmentIcon sx={{ fontSize: 12, color: primaryColor }} />
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, color: primaryColor, textTransform: 'uppercase' }}>High Demand</Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Pricing and Action Section (Matches SS1) */}
                        <Box sx={{ mt: 3, pt: 3, borderTop: isLight ? '1px dashed #e2e8f0' : '1px dashed #27272a' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Typography sx={{
                              fontSize: '1rem',
                              fontWeight: 700,
                              color: isLight ? '#64748b' : '#52525b',
                              textDecoration: 'line-through',
                              opacity: 0.8
                            }}>
                              ₹{getPrice(modKey).mrp}
                            </Typography>
                            <Typography sx={{
                              fontSize: '1.75rem',
                              fontWeight: 900,
                              color: isLight ? '#0f172a' : '#f8fafc',
                              display: 'flex',
                              alignItems: 'baseline'
                            }}>
                              <Box component="span" sx={{ fontSize: '1rem', mr: 0.5 }}>₹</Box>
                              {getPrice(modKey).price}
                            </Typography>
                            <Box sx={{
                              px: 1.2,
                              py: 0.4,
                              borderRadius: '6px',
                              bgcolor: 'rgba(16, 185, 129, 0.1)',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              ml: 'auto'
                            }}>
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Best Value</Typography>
                            </Box>
                          </Box>

                          <Button
                            fullWidth
                            variant="contained"
                            disabled={isComingSoon}
                            onClick={(e) => {
                              const basePath = location.pathname.substring(0, location.pathname.lastIndexOf('/'));
                              navigate(`${basePath}/${modKey}`);
                            }}
                            sx={{
                              py: 2,
                              borderRadius: '16px',
                              fontSize: '1rem',
                              fontWeight: 800,
                              textTransform: 'none',
                              background: isComingSoon
                                ? (isLight ? '#f1f5f9' : '#1f1f22')
                                : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}ee 100%)`,
                              color: isComingSoon ? (isLight ? '#94a3b8' : '#52525b') : '#fff',
                              boxShadow: isComingSoon ? 'none' : `0 8px 20px -6px ${primaryColor}60`,
                              transition: 'all 0.3s ease',
                              '&:hover': !isComingSoon ? {
                                background: `linear-gradient(135deg, ${primaryColor}ee 0%, ${primaryColor} 100%)`,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 12px 24px -8px ${primaryColor}80`,
                              } : {},
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1
                            }}
                          >
                            {isComingSoon ? 'Launching Soon' : 'Get Access'}
                            {!isComingSoon && <ArrowRightAltIcon sx={{ fontSize: 20 }} />}
                          </Button>
                        </Box>
                      </Paper>
                    </motion.div>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Main Content (Conditionally Hidden for Launcher) */}
          {categoryId !== 'aiInterviewPrep' ? (
            <>
              {/* Search Bar */}
              {categoryId !== 'completeInterviewKit' && (
                <Box sx={{ maxWidth: 540, mx: 'auto', mt: 4 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: isLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(24, 24, 27, 0.4)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid',
                    borderColor: isLight ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '16px',
                    px: 2.5,
                    py: 1.25,
                    boxShadow: isLight
                      ? '0 10px 25px -5px rgba(0,0,0,0.05), 0 0 15px rgba(59, 130, 246, 0.05)'
                      : '0 20px 40px -12px rgba(0,0,0,0.5), 0 0 20px rgba(59, 130, 246, 0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: isLight ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.3)',
                      boxShadow: isLight
                        ? '0 15px 30px -8px rgba(0,0,0,0.08), 0 0 20px rgba(59, 130, 246, 0.1)'
                        : '0 25px 50px -12px rgba(0,0,0,0.7), 0 0 30px rgba(59, 130, 246, 0.2)',
                    },
                    '&:focus-within': {
                      borderColor: '#3b82f6',
                      bgcolor: isLight ? '#fff' : 'rgba(24, 24, 27, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: isLight
                        ? '0 20px 40px -10px rgba(59, 130, 246, 0.2), 0 0 40px rgba(59, 130, 246, 0.1)'
                        : '0 30px 60px -15px rgba(0,0,0,0.8), 0 0 50px rgba(59, 130, 246, 0.25)'
                    }
                  }}>
                    <SearchIcon sx={{ color: '#3b82f6', fontSize: 22, opacity: 0.9 }} />
                    <InputBase
                      placeholder={`Search topics in ${categoryData.title}...`}
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{
                        fontSize: '0.85rem',
                        color: isLight ? '#0f172a' : '#f8fafc',
                        '& input::placeholder': { opacity: 0.7 }
                      }}
                    />
                  </Box>
                </Box>
              )}

              {!isPurchased && !isFree && isBundleAdvantageous && (
                <Box sx={{
                  mt: 4,
                  mb: 8,
                  p: 3,
                  borderRadius: 4,
                  background: isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.03)',
                  border: '1px dashed #3b82f660',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#3b82f6' }}>
                    <LockIcon sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="800">PREVIEW MODE</Typography>
                  </Box>
                  <Typography sx={{ color: isLight ? '#475569' : '#a1a1aa', fontSize: '0.9rem', maxWidth: 500, textAlign: 'center' }}>
                    {isFree ? (
                      <>Sign in to get full 1-year access to all interactive visualizers and interview modules for <strong>{categoryData.title}</strong>.</>
                    ) : (
                      <>This track is locked. Unlock it now to get full 1-year access to all interactive visualizers and interview modules for <strong>{categoryData.title}</strong>.</>
                    )}
                  </Typography>
                  <Button
                    onClick={handlePurchase}
                    disabled={loadingProductId === categoryId}
                    variant="contained"
                    startIcon={<FlashOnIcon />}
                    sx={{
                      mt: 1,
                      px: 6, py: 1.5, borderRadius: 2, fontWeight: 900, textTransform: 'none',
                      bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' },
                      boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)',
                      '&.Mui-disabled': {
                        opacity: 0.7,
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}
                  >
                    {loadingProductId === categoryId
                      ? 'PROCESSING...'
                      : isFree
                        ? 'SIGN IN TO ACCESS'
                        : `UNLOCK FULL ACCESS • ₹${getPrice(categoryId).price}`
                    }
                    {!isFree && getPrice(categoryId).mrp && loadingProductId !== categoryId && (
                      <Box component="span" sx={{ textDecoration: 'line-through', ml: 1, opacity: 0.7, fontSize: '0.85em', fontWeight: 500 }}>
                        ₹{getPrice(categoryId).mrp}
                      </Box>
                    )}
                  </Button>
                </Box>
              )}

              {categoryId === 'foundation' ? (
                <FoundationRoadmap
                  categoryData={categoryData}
                  isLight={isLight}
                  isPurchased={isPurchased}
                  previewIds={[]}
                  navigate={navigate}
                  setAlertMessage={setAlertMessage}
                  setAlertOpen={setAlertOpen}
                  searchQuery={searchQuery}
                  getBadgeStyle={getBadgeStyle}
                />
              ) : categoryData.isTrackLink ? (
                /* MEGA BUNDLE PREMIUM GRID VIEW */
                <Box sx={{ mt: 2 }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                    gap: 3,
                    px: { xs: 0, sm: 1 },
                    maxWidth: 1600,
                    mx: 'auto'
                  }}>
                    {(TRACKS[categoryId]?.categories || []).map((modKey, index) => {
                      const modData = CATALOG[modKey];
                      if (!modData) return null;

                      const primaryColor = modKey === 'react' ? '#0ea5e9' : modKey === 'angular' ? '#6366f1' : modKey === 'javascript' ? '#14b8a6' : modKey === 'frontendSystemDesign' ? '#10b981' : '#8b5cf6';
                      const modIsPurchased = (isFree && !!user) || user?.purchasedModules?.includes(modKey) || isPurchased;

                      return (
                        <motion.div
                          key={modKey}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          style={{ display: 'flex' }}
                        >
                          <Paper
                            elevation={0}
                            onClick={() => {
                              // Global Freemium: Allow navigation to sub-category topics regardless of purchase
                              // This allows users to see what's inside (Free Preview)
                              navigate(`${basePath}/${modKey}`);
                            }}
                            sx={{
                              display: 'flex', flexDirection: 'column', height: '100%', width: '100%', p: { xs: 3, md: 4 }, borderRadius: 4,
                              background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(23, 23, 26, 0.8)',
                              backdropFilter: 'blur(16px) saturate(180%)',
                              border: isLight ? `1px solid ${primaryColor}25` : `1px solid ${primaryColor}30`,
                              cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                              position: 'relative', overflow: 'visible',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: isLight ? `0 20px 40px -10px ${primaryColor}30` : `0 20px 40px -10px ${primaryColor}40`,
                                border: `1px solid ${primaryColor}`,
                              }
                            }}
                          >
                            {/* Badge pill */}
                            {TRACK_BADGES[modKey] && (
                              <Box sx={{
                                position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                                background: TRACK_BADGES[modKey].gradient,
                                color: '#fff', px: 2, py: 0.5, borderRadius: '20px',
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                boxShadow: `0 4px 12px ${TRACK_BADGES[modKey].shadow}`,
                                zIndex: 5, whiteSpace: 'nowrap'
                              }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1px' }}>
                                  {TRACK_BADGES[modKey].label}
                                </Typography>
                              </Box>
                            )}
                            {/* Header Section */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: TRACK_BADGES[modKey] ? 1 : 0 }}>
                              <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 44, height: 44, borderRadius: '14px',
                                background: isLight ? `${primaryColor}15` : `${primaryColor}20`,
                                color: primaryColor,
                                '& svg': { fontSize: '24px !important' }
                              }}>
                                {modData.icon}
                              </Box>
                              <Box>
                                <Typography sx={{ color: primaryColor, fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                  {modData.subtitle || 'FRAMEWORK EXCELLENCE'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: isLight ? '#0f172a' : '#f8fafc', fontSize: '1.35rem', letterSpacing: '-0.5px' }}>
                                  {modData.title}
                                </Typography>
                              </Box>
                            </Box>

                            <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '0.95rem', mb: 4, flex: 1, fontWeight: 500, lineHeight: 1.6 }}>
                              {modData.description}
                            </Typography>

                            {/* Features List */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                              {(modData.features || []).slice(0, 4).map((feat, fidx) => (
                                <Box key={fidx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <CheckCircleIcon sx={{ fontSize: 18, color: primaryColor }} />
                                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: isLight ? '#334155' : '#cbd5e1' }}>
                                    {feat}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* Quick Syllabus Peek */}
                            {!modIsPurchased && !isFree && modData.topics && modData.topics.length > 0 && (
                              <Box sx={{ borderTop: isLight ? '1px dashed #e2e8f0' : '1px dashed #27272a', py: 2, mb: 4, position: 'relative' }}>
                                <Box
                                  onClick={(e) => { e.stopPropagation(); setExpandedMods(prev => ({ ...prev, [modKey]: !prev[modKey] })); }}
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                                >
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: isLight ? '#475569' : '#a1a1aa', letterSpacing: '1px' }}>
                                    QUICK SYLLABUS PEEK
                                  </Typography>
                                  {expandedMods[modKey]
                                    ? <ExpandLessIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                    : <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} />}
                                </Box>
                                {expandedMods[modKey] && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: -32, right: -32,
                                    zIndex: 20,
                                    bgcolor: isLight ? '#ffffff' : '#16161b',
                                    border: isLight ? '1px solid #e2e8f0' : '1px solid #2d2d33',
                                    borderRadius: 2,
                                    p: 2,
                                    boxShadow: isLight ? '0 8px 24px rgba(0,0,0,0.1)' : '0 8px 24px rgba(0,0,0,0.5)',
                                  }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {modData.topics.slice(0, 6).map((t, tidx) => (
                                        <Typography key={tidx} sx={{ fontSize: '0.82rem', color: isLight ? '#64748b' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: primaryColor, opacity: 0.6, flexShrink: 0 }} />
                                          {t.title}
                                        </Typography>
                                      ))}
                                      {modData.topics.length > 6 && (
                                        <Typography sx={{ fontSize: '0.75rem', color: primaryColor, fontWeight: 700, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                          + {modData.topics.length - 6} more deep-dive modules
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Social Proof & Pricing area */}
                            <Box sx={{ mt: 'auto' }}>
                              {!modIsPurchased && !isFree && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 11, border: `2px solid ${isLight ? '#fff' : '#18181b'}`, bgcolor: isLight ? '#f1f5f9' : '#27272a' } }}>
                                      <Avatar /> <Avatar /> <Avatar /> <Avatar />
                                    </AvatarGroup>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: isLight ? '#64748b' : '#a1a1aa' }}>
                                      +{((modKey || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 200) + 300} ENROLLED
                                    </Typography>
                                  </Box>
                                  <Chip
                                    icon={<LocalFireDepartmentIcon sx={{ color: '#ef4444 !important', fontSize: '14px !important' }} />}
                                    label="High Demand"
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.65rem', fontWeight: 800, background: isLight ? '#fee2e2' : '#7f1d1d40', color: isLight ? '#b91c1c' : '#fca5a5', border: isLight ? '1px solid #f8717150' : '1px solid #ef444450' }}
                                  />
                                </Box>
                              )}

                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                {!modIsPurchased && !isFree && getPrice(modKey).price > 0 && (
                                  <Typography sx={{ color: isLight ? '#0f172a' : '#f8fafc', fontWeight: 900, fontSize: '1.6rem', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getPrice(modKey).mrp > 0 && (
                                      <Typography component="span" sx={{ fontSize: '1rem', color: isLight ? '#94a3b8' : '#71717a', textDecoration: 'line-through', fontWeight: 600 }}>
                                        ₹{getPrice(modKey).mrp}
                                      </Typography>
                                    )}
                                    ₹{getPrice(modKey).price}
                                    <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#10b981', background: '#10b98120', px: 1, py: 0.5, borderRadius: 1, ml: 1 }}>Best Value</Box>
                                  </Typography>
                                )}
                              </Box>

                              <Button
                                fullWidth
                                variant="contained"
                                disabled={loadingProductId === modKey}
                                sx={{
                                  py: 1.5, borderRadius: '14px',
                                  background: (modIsPurchased || isFree || getPrice(modKey).price === 0) ? `${primaryColor}15` : `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                  color: (modIsPurchased || isFree || getPrice(modKey).price === 0) ? primaryColor : '#fff',
                                  fontWeight: 900, fontSize: '1rem', textTransform: 'none',
                                  boxShadow: (modIsPurchased || isFree || getPrice(modKey).price === 0) ? 'none' : `0 10px 20px -8px ${primaryColor}80`,
                                  '&:hover': {
                                    background: (modIsPurchased || isFree || getPrice(modKey).price === 0) ? `${primaryColor}25` : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              >
                                {loadingProductId === modKey ? 'Processing...' : (modIsPurchased || isFree || getPrice(modKey).price === 0) ? 'Access Curriculum' : 'Get Access →'}
                              </Button>
                            </Box>
                          </Paper>
                        </motion.div>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 8 }}>
                  {orderedGroups.map((groupName) => {
                    const z2hProgress = categoryId === 'foundation' ? parseInt(localStorage.getItem('foundation_progress') || '1', 10) : 1;
                    const query = searchQuery.toLowerCase();
                    const groupTopics = categoryData.topics.filter(t =>
                      t.group === groupName && (
                        !query ||
                        t.title.toLowerCase().includes(query) ||
                        t.desc.toLowerCase().includes(query) ||
                        t.badge?.toLowerCase().includes(query)
                      )
                    );
                    if (groupTopics.length === 0) return null;

                    return (
                      <Box key={groupName || 'Other'} id={groupName}>
                        {groupName && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Typography variant="caption" fontWeight="800" sx={{
                              color: isLight ? '#64748b' : '#71717a', fontSize: '0.68rem', letterSpacing: '1.2px', textTransform: 'uppercase'
                            }}>
                              {groupName === 'Practice' ? 'Practice Sessions' : groupName === 'Final Challenge' ? 'Final Challenge' : groupName}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: isLight ? '#e2e8f0' : '#1f1f22' }} />
                          </Box>
                        )}

                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(auto-fill, minmax(260px, 1fr))',
                            md: 'repeat(auto-fill, minmax(260px, 1fr))'
                          },
                          gap: 2.5
                        }}>
                          {groupTopics.flatMap(topic => {
                            // Unpack sub-problems for deep tracks to match Angular's high-density look
                            const unpackingTracks = ['angular', 'react', 'frontendSystemDesign', 'interviewPrep', 'systemDesign'];
                            if (unpackingTracks.includes(categoryId) && topic.problems && topic.problems.length > 0) {
                              return topic.problems.map(p => ({
                                ...p,
                                parentTopicId: topic.id,
                                parentTitle: topic.title,
                                icon: topic.icon // Inherit parent icon
                              }));
                            }
                            return [topic];
                          }).map((topic) => {
                            const badgeStyle = getBadgeStyle(topic.badge || '', isLight);
                            const isFreePreview = previewIds.includes(topic.id);

                            let isAccessible = isPurchased || isFreePreview;
                            // Track whether this topic is locked due to phase sequencing (vs. purchase)
                            let isPhaseLocked = false;

                            // Foundation course: phase-sequential locking applies to ALL users (purchased or not)
                            // Purchased users must complete Phase N before accessing Phase N+1
                            if (categoryId === 'foundation') {
                              const phaseMatch = groupName && groupName.match(/^Phase\s+(\d+)$/);
                              if (phaseMatch) {
                                const phaseNum = parseInt(phaseMatch[1], 10);
                                if (phaseNum > z2hProgress) {
                                  isAccessible = false;
                                  isPhaseLocked = true;
                                }
                              }
                            }

                            return (
                              <Paper
                                key={topic.id}
                                elevation={0}
                                onClick={() => {
                                  if (!isAccessible) {
                                    if (isPhaseLocked) {
                                      const prevPhaseNum = groupName.match(/^Phase\s+(\d+)$/)?.[1];
                                      const prevNum = prevPhaseNum ? parseInt(prevPhaseNum, 10) - 1 : null;
                                      setAlertMessage(prevNum && prevNum > 0
                                        ? `🔒 Complete Phase ${prevNum} first to unlock this phase.`
                                        : '🔒 Complete the current phase to unlock the next one.');
                                    } else {
                                      setAlertMessage('🔒 Locked — Purchase to unlock full access.');
                                    }
                                    setAlertOpen(true);
                                    return;
                                  }
                                  navigate(`${basePath}/${topic.id}`);
                                }}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%',
                                  p: 2.5,
                                  borderRadius: 2.5,
                                  bgcolor: isLight ? '#ffffff' : '#111113',
                                  border: isLight ? (isFreePreview && !isPurchased ? '1px solid #10b98150' : '1px solid #e2e8f0') : (isFreePreview && !isPurchased ? '1px solid #10b98150' : '1px solid #1f1f22'),
                                  cursor: isAccessible ? 'pointer' : 'not-allowed',
                                  opacity: isAccessible ? 1 : 0.6,
                                  filter: isAccessible ? 'none' : 'grayscale(0.5)',
                                  textAlign: 'left',
                                  position: 'relative',
                                  transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': isAccessible ? {
                                    transform: 'translateY(-4px)',
                                    borderColor: '#3b82f640',
                                    bgcolor: isLight ? '#ffffff' : '#141417',
                                    boxShadow: isLight ? '0 12px 24px -10px rgba(0,0,0,0.1)' : '0 12px 24px -10px rgba(0,0,0,0.5)',
                                  } : {}
                                }}
                              >
                                {!isAccessible && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: isLight ? '#94a3b8' : '#64748b',
                                    bgcolor: isLight ? '#f1f5f9' : '#1e1e21',
                                    p: 0.5,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    border: isLight ? '1px solid #e2e8f0' : '1px solid #27272a',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                  }}>
                                    <LockIcon sx={{ fontSize: 14 }} />
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}>
                                  {topic.icon && (
                                    <Box sx={{ p: 0.75, bgcolor: isLight ? '#f1f5f9' : '#0d0d10', borderRadius: 1.5, border: isLight ? '1px solid #e2e8f0' : '1px solid #27272a', display: 'flex' }}>
                                      <Box sx={{ '& svg': { fontSize: '18px !important', color: 'primary.main' } }}>
                                        {topic.icon}
                                      </Box>
                                    </Box>
                                  )}
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1, pr: !isAccessible ? 3.5 : 0 }}>
                                      <Typography variant="body2" fontWeight="800" sx={{ color: isLight ? '#0f172a' : '#e2e8f0', lineHeight: 1.3, fontSize: '0.95rem' }}>
                                        {topic.title}
                                      </Typography>
                                      {topic.badge && (
                                        <Chip
                                          label={topic.badge}
                                          size="small"
                                          sx={{
                                            height: 18, fontSize: '0.68rem', fontWeight: 700,
                                            bgcolor: badgeStyle.bg, color: badgeStyle.color,
                                            border: `1px solid ${badgeStyle.border}`,
                                            borderRadius: '4px', flexShrink: 0,
                                            '& .MuiChip-label': { px: 1 }
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>

                                <Typography variant="body2" sx={{ color: isLight ? '#475569' : '#a1a1aa', mb: 1.5, flex: 1, lineHeight: 1.5, fontSize: '0.88rem', display: 'block' }}>
                                  {topic.desc}
                                </Typography>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 1.5, borderTop: isLight ? '1px solid #f1f5f9' : '1px solid #1f1f22' }}>
                                  <Box />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isFreePreview && !isPurchased && (
                                      <Box sx={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, background: '#10b98115', borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.5px' }}>FREE PREVIEW</Typography>
                                      </Box>
                                    )}
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color: isAccessible ? 'primary.main' : '#64748b',
                                      bgcolor: isLight ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)',
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s ease'
                                    }}>
                                      <Typography variant="caption" fontWeight="800" sx={{ textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.75rem' }}>
                                        {isPurchased ? 'Open' : 'Launch'}
                                      </Typography>
                                      <ChevronRightIcon sx={{ fontSize: 13 }} />
                                    </Box>
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {categoryData.topics.filter(t =>
                    !searchQuery ||
                    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    t.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    t.badge.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: isLight ? '#0f172a' : '#f8fafc', mb: 1 }}>No topics found</Typography>
                        <Typography sx={{ color: isLight ? '#64748b' : '#a1a1aa', fontSize: '0.9rem' }}>Try searching with a different keyword</Typography>
                        <Button
                          variant="text"
                          onClick={() => setSearchQuery('')}
                          sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
                        >
                          Clear Search
                        </Button>
                      </Box>
                    )}
                </Box>
              )}
            </>
          ) : null}

        </Box>
      </Box>

      {/* Snackbar Alert */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', fontWeight: 700 }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', pb: 3, borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', pt: 3, mt: 4, width: '100%' }}>
        <Typography variant="caption" sx={{ color: isLight ? '#64748b' : '#52525b', fontSize: '0.7rem' }}>
          FlowState · Interactive System Design, DSA & Web Architecture Learning
        </Typography>
      </Box>
    </Box>
  );
};

export default TopicSelection;